import React from 'react';
import { Modal, Button, Typography, Space, Tag, Divider } from 'antd';
import { ThunderboltOutlined, CrownFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface IndicatorLimitModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  currentPlanLabel: string;
  recommendedPlanLabel: string;
  currentLimit: number;
  selectedCount: number;
}

const IndicatorLimitModal: React.FC<IndicatorLimitModalProps> = ({
  open,
  onClose,
  onUpgrade,
  currentPlanLabel,
  recommendedPlanLabel,
  currentLimit,
  selectedCount,
}) => {
  const { t, i18n } = useTranslation();

  const headline =
    i18n.language === 'zh-CN'
      ? '更多指标，更多交易灵感'
      : 'More indicators, more trading possibilities';

  const description =
    i18n.language === 'zh-CN'
      ? `你已添加 ${selectedCount} 个指标，已达到当前套餐上限（${currentLimit} 个）。升级即可解锁全部指标。`
      : `You’ve applied ${selectedCount} indicators — the maximum for your plan (${currentLimit}). Upgrade to unlock all indicators.`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={560}
      bodyStyle={{ padding: '24px 32px 16px' }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #111827 0%, #0f172a 40%, #111827 100%)',
          borderRadius: 12,
          padding: 18,
          marginBottom: 18,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 20%, rgba(82, 196, 26, 0.25), transparent 35%), radial-gradient(circle at 80% 10%, rgba(37, 99, 235, 0.2), transparent 35%)',
            pointerEvents: 'none',
          }}
        />
        <Space align="center" size={12} style={{ position: 'relative' }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(82, 196, 26, 0.15)',
              display: 'grid',
              placeItems: 'center',
              color: '#52c41a',
              fontSize: 22,
            }}
          >
            <ThunderboltOutlined />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: '#e5e7eb' }}>
              {headline}
            </Title>
            <Text style={{ color: '#cbd5e1' }}>{description}</Text>
          </div>
        </Space>
      </div>

      <Space
        direction="vertical"
        size={14}
        style={{ width: '100%', marginBottom: 16 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
          }}
        >
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: 12,
            }}
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              {t('indicator.limit.currentPlan')}
            </Text>
            <Space align="center" size={8}>
              <Tag>{currentPlanLabel}</Tag>
              <Text strong>{currentLimit}</Text>
            </Space>
          </div>

          <div
            style={{
              border: '1px solid #1677ff',
              borderRadius: 12,
              padding: 12,
              background: 'rgba(22, 119, 255, 0.04)',
            }}
          >
            <Space align="center" size={8} style={{ marginBottom: 4 }}>
              <Text type="secondary">{t('indicator.limit.recommendedPlan')}</Text>
              <CrownFilled style={{ color: '#faad14' }} />
            </Space>
            <Space align="center" size={8}>
              <Tag color="blue">{recommendedPlanLabel}</Tag>
              <Text strong>{t('indicator.limit.unlimited')}</Text>
            </Space>
          </div>
        </div>

        <Divider style={{ margin: '8px 0 0' }} />

        <div style={{ display: 'grid', gap: 8 }}>
          <Text strong>{t('indicator.limit.upgradeBenefits')}</Text>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#4b5563' }}>
            <li>{t('indicator.limit.benefitAllIndicators')}</li>
            <li>{t('indicator.limit.benefitPriority')}</li>
            <li>{t('indicator.limit.benefitFaster')}</li>
          </ul>
        </div>
      </Space>

      <Button type="primary" block size="large" onClick={onUpgrade}>
        {t('indicator.limit.cta')}
      </Button>
    </Modal>
  );
};

export default IndicatorLimitModal;
